/** biome-ignore-all lint/a11y/noStaticElementInteractions: gallery uses pointer gestures */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: gallery taps are handled by existing controls */

import { type MouseEvent as ReactMouseEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CarouselBullets } from "./CarouselBullets";
import { CarouselAsset } from "./GalleryAsset";
import GalleryModeIcon from "./GalleryModeIcon";
import { useGlobalContext } from "./GlobalStateProvider";
import GridGallery from "./GridGallery";
import ModalDrawer, { type InteractiveDismissHandle } from "./InteractiveDismiss";
import { useCenterItem } from "./useCenterActiveList";
import useDevice from "./useDevice";
export default function MobileGallery({
  behavior
}: {
  behavior: "smooth" | "instant";
}) {
  const {
    assets,
    setModalIndex,
    modalIndex,
    selected,
    setModalOpen
  } = useGlobalContext();
  const {
    scrollRef,
    itemRefs,
    scrollToIndex,
    goPrev,
    goNext
  } = useCenterItem({
    initial: modalIndex,
    behavior,
    onCenterChange: setModalIndex
  });
  const [showGrid, setShowGrid] = useState(false);
  const {
    width: screenWidth
  } = useDevice();
  const isMd = screenWidth >= 768;
  const modal = useRef<InteractiveDismissHandle>(null);
  const pendingIndexRef = useRef<number | null>(null);
  const [containerH, setContainerH] = useState(0);
  useLayoutEffect(() => {
    setContainerH(scrollRef.current?.clientHeight || 500);
  }, [scrollRef.current?.clientHeight]);
  useEffect(() => {
    if (showGrid || pendingIndexRef.current === null) return;
    const frame = requestAnimationFrame(() => {
      const index = pendingIndexRef.current;
      if (index === null) return;
      scrollToIndex(index, "instant");
      pendingIndexRef.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToIndex, showGrid]);
  function handleClick(e: ReactMouseEvent<HTMLDivElement, MouseEvent>) {
    e.preventDefault();
    e.stopPropagation();
    const {
      left,
      width
    } = e.currentTarget.getBoundingClientRect();
    if (e.clientX < left + width / 2) {
      goPrev();
    } else {
      goNext();
    }
  }
  useEffect(() => {
    const handleKeyDown = (e_0: KeyboardEvent) => {
      if (e_0.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setModalOpen]);
  useLayoutEffect(() => {
    const measuredHeight = scrollRef.current?.clientHeight ?? 500;
    setContainerH(measuredHeight);
  }, [scrollRef]);
  return <ModalDrawer ref={modal} disableAnimations={behavior === "instant"} onDismiss={() => setModalOpen(false)} horizontalScrollRef={scrollRef} // hack hack
  className="fixed inset-0 z-10 flex h-dvh w-screen items-center justify-center overflow-hidden overscroll-none bg-white">
			<div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-2 bg-white md:gap-5.25">
				{/* Control Bar Top */}
				<div className="relative mx-auto flex w-full items-center justify-center landscape:max-w-[calc(80dvh*16/9)]">
					<button type="button" aria-label="Show Grid" className="cursor-pointer opacity-30 md:hidden" onClick={e_1 => {
          e_1.stopPropagation();
          setShowGrid(prev => !prev);
        }}>
						<span aria-hidden="true">
							<GalleryModeIcon showGrid={showGrid} />
						</span>
					</button>
					<button type="button" aria-label="Close Modal" className="relative hidden cursor-pointer opacity-50 hover:opacity-70 data-[grid=true]:invisible md:block" data-grid={showGrid} onClick={e_2 => {
          e_2.preventDefault();
          e_2.stopPropagation();
          modal.current?.dismiss();
        }}>
						<span aria-hidden="true" className="hover:text-active text-not-active absolute top-1.5">
							{/* this is used to move the bottom a little down */}
							close
						</span>
						<span aria-hidden="true" className="invisible">
							{/* todo: hack hack: this is used to give height to the button */}
							close
						</span>
					</button>
				</div>
				{/* Main Gallery */}
				<div className={`flex h-[83dvh] w-full flex-col gap-3.25 md:h-[70dvh] md:gap-5.25 ${showGrid && "pointer-events-none hidden"}`} aria-hidden={showGrid} onClick={handleClick}>
					<div ref={scrollRef} className="scrollbar-hide flex w-dvw grow touch-pan-x snap-x snap-mandatory items-center gap-2 overflow-x-auto overflow-y-hidden xl:gap-5.25">
						{assets.map((asset, i) => <div key={asset._type === "film" ? asset.vimeo : asset.url} ref={ref => {
            itemRefs.current[i] = ref;
          }} className="flex size-full shrink-0 snap-center items-center justify-center px-2">
								<CarouselAsset asset={asset} containerW={screenWidth - 16} // 8 padding on left and right
            containerH={containerH} maxWRatio={!isMd ? 1 : 0.7}
            // maxHRatio={!isMd ? 0.83 : 0.7}
            maxHRatio={1} />
							</div>)}
					</div>
					<div className="flex h-fit w-full items-center justify-center">
						<CarouselBullets array={assets} activeIndex={modalIndex} setActiveIndex={index_0 => scrollToIndex(index_0, behavior)} className={`${showGrid ? "pointer-events-none hidden" : "pointer-events-auto"}`} aria-hidden={showGrid} />
					</div>
				</div>
				{/* Main Grid */}
				<div className={`scrollbar-hide h-[83dvh] w-dvw overflow-y-scroll pb-35.75 md:h-[70dvh] md:w-[70dvw] ${!showGrid && "pointer-events-none hidden"}`} aria-hidden={!showGrid}>
					<GridGallery images={assets} title={selected?.project?.title ?? "Project"} onSelect={index_1 => {
          setModalIndex(index_1);
          pendingIndexRef.current = index_1;
          setShowGrid(false);
        }} />
				</div>
				{/* Control Bar Bottom */}
				<div className="relative mx-auto flex w-full items-center justify-center landscape:max-w-[calc(80dvh*16/9)]">
					<button type="button" aria-label="Show Grid" className="hidden cursor-pointer opacity-30 md:block" onClick={e_3 => {
          e_3.stopPropagation();
          setShowGrid(prev_0 => !prev_0);
        }}>
						<span aria-hidden="true">
							<GalleryModeIcon showGrid={showGrid} />
						</span>
					</button>
				</div>
			</div>
		</ModalDrawer>;
}